import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Supported social platforms
 */
export type SocialPlatform = "twitter" | "facebook" | "instagram";

/**
 * SDK configuration contract
 */
interface SdkConfig {
  url: string;
  renderFn: () => void;
}

/**
 * Platform → SDK configuration map
 */
const SDK_CONFIG: Record<SocialPlatform, SdkConfig> = {
  twitter: {
    url: "https://platform.twitter.com/widgets.js",
    renderFn: () => {
      window.twttr?.widgets?.load();
    },
  },
  facebook: {
    url: "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v18.0",
    renderFn: () => {
      window.FB?.XFBML?.parse();
    },
  },
  instagram: {
    url: "//www.instagram.com/embed.js",
    renderFn: () => {
      window.instgrm?.Embeds?.process();
    },
  },
};

/**
 * Hook return type
 */
interface UseSocialEmbedLoaderResult {
  containerRef: React.RefObject<HTMLDivElement>;
  sdkLoaded: boolean;
}

/**
 * React hook to dynamically load and initialize social media embed SDKs
 */
export const useSocialEmbedLoader = (
  platform: SocialPlatform
): UseSocialEmbedLoaderResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sdkLoaded, setSdkLoaded] = useState<boolean>(false);

  const config = SDK_CONFIG[platform];

  const loadScript = useCallback((): void | (() => void) => {
    // Prevent duplicate script injection
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${config.url}"]`
    );

    if (existingScript) {
      config.renderFn();
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = config.url;
    script.async = true;
    script.id = `${platform}-sdk`;

    const handleLoad = () => {
      config.renderFn();
      setSdkLoaded(true);
    };

    const handleError = (event: Event) => {
      console.error(`Failed to load ${platform} SDK`, event);
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, [platform, config]);

  useEffect(() => {
    const cleanup = loadScript();
    return cleanup;
  }, [loadScript]);

  /**
   * Re-run render when SDK is already loaded
   * (important for dynamic embeds / rerenders)
   */
  useEffect(() => {
    if (!sdkLoaded || !containerRef.current) return;

    const timeoutId = window.setTimeout(() => {
      config.renderFn();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [sdkLoaded, config]);

  return { containerRef, sdkLoaded };
};
